import type { FlowEditorState, FlowNodeKind, WidgetConfigState, WidgetTab } from "./types";
import { DEFAULT_WIDGET_IMAGE, RGL_CONTAINER_PADDING } from "./constants";
import { clampValue } from "./widgetUtils";
export const getDefaultNodeText = (tab: WidgetTab, config: WidgetConfigState) => {
  if (tab === "alert") return config.alert.messageTemplate;
  if (tab === "goal") return `${config.goal.current}/${config.goal.target}`;
  if (tab === "leaderboard") return config.leaderboard.title;
  if (tab === "chat") return config.chat.title;
  return config.timer.label;
};

export const getDefaultNodeSettings = (
  kind: FlowNodeKind,
  config: WidgetConfigState,
): Record<string, any> => {
  if (kind === "alert") {
    return {
      theme: config.alert.theme,
      layoutPreset: config.alert.layoutPreset,
      messageTemplate: config.alert.messageTemplate,
      minAmount: Number(config.alert.minAmount || 0),
      displayMs: Math.max(Number(config.alert.duration) || 0, 0) * 1000,
      animation: config.alert.animation,
      effect: config.alert.effect,
      soundName: config.alert.soundName,
      volume: config.alert.volume,
      imageUrl: config.alert.imageUrl,
      iconMode: config.alert.iconMode,
      iconUrl: config.alert.iconUrl || config.alert.imageUrl,
      iconEmoji: config.alert.iconEmoji,
      iconShape: config.alert.iconShape,
      iconSize: config.alert.iconSize,
      backgroundStyle: config.alert.backgroundStyle,
      textColor: config.alert.textColor,
      showDonorName: config.alert.showDonorName,
      showAmount: config.alert.showAmount,
      showMessage: config.alert.showMessage,
      showIcon: config.alert.showIcon,
      showBorderGlow: config.alert.showBorderGlow,
      currency: "LAK",
    };
  }

  if (kind === "goal") {
    return {
      title: config.goal.title,
      current: Number(config.goal.current || 0),
      target: Number(config.goal.target || 0),
      endsAt: config.goal.endsAt,
      layout: config.goal.layout,
      showPercent: config.goal.showPercent,
      currency: "LAK",
    };
  }

  if (kind === "leaderboard") {
    return {
      title: config.leaderboard.title,
      period: config.leaderboard.period,
      limit: Number(config.leaderboard.limit || 5),
      showAmounts: config.leaderboard.showAmounts,
      currency: "LAK",
    };
  }

  if (kind === "timer") {
    return {
      status: config.timer.status,
      label: config.timer.label,
      durationMinutes: Number(config.timer.durationMinutes || 0),
      breakMinutes: Number(config.timer.breakMinutes || 0),
      layout: config.timer.layout,
      opacity: config.timer.opacity,
      scale: config.timer.scale,
      showProgress: config.timer.showProgress,
      allowMinus: config.timer.allowMinus,
    };
  }

  if (kind === "chat") {
    return {
      title: config.chat.title,
      width: Number(config.chat.width || 420),
      height: Number(config.chat.height || 620),
      fontSize: Number(config.chat.fontSize || 14),
      theme: config.chat.theme,
      showAvatar: config.chat.showAvatar,
      showBadges: config.chat.showBadges,
    };
  }

  if (kind === "image") {
    return {
      imageUrl: DEFAULT_WIDGET_IMAGE,
      fit: "cover",
    };
  }

  if (kind === "amount") {
    return {
      amount: 25000,
      currency: "LAK",
    };
  }

  return {
    fontSize: 18,
    fontWeight: "bold",
  };
};

export const getFlowNodeSize = (kind: FlowNodeKind) => {
  if (kind === "goal") return { width: 280, height: 94 };
  if (kind === "leaderboard") return { width: 260, height: 180 };
  if (kind === "timer") return { width: 220, height: 118 };
  if (kind === "chat") return { width: 260, height: 190 };
  if (kind === "image") return { width: 140, height: 140 };
  if (kind === "alert") return { width: 260, height: 132 };
  return { width: 180, height: 82 };
};

export const getCompactNodeWidth = (
  kind: FlowNodeKind | undefined,
  viewport: FlowEditorState["viewport"],
) => {
  const widthByKind: Record<FlowNodeKind, number> = {
    alert: 220,
    goal: 220,
    leaderboard: 240,
    timer: 180,
    chat: 240,
    text: 160,
    image: 120,
    amount: 140,
  };
  const fallbackWidth = widthByKind.text;
  const nextWidth = kind ? widthByKind[kind] : fallbackWidth;

  return clampValue(
    nextWidth,
    80,
    viewport.width - RGL_CONTAINER_PADDING[0] * 2,
  );
};
